import React, { useState, useRef, useEffect, useCallback } from 'react';
import { videoPreloader, getCachedVideo } from '../../lib/videoPreloader';
import { logVideoLoadStart, logVideoLoadEnd } from '../../lib/performanceMonitor';
import { getOptimizedVideoUrl } from '../../lib/optimizedAssets';

interface LazyVideoProps {
  darkSrc: string;
  lightSrc: string;
  videoKey?: string; // Optional: key to look up optimized version
  isDark: boolean;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  poster?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  threshold?: number;
  rootMargin?: string;
  preload?: 'none' | 'metadata' | 'auto';
}

export function LazyVideo({
  darkSrc,
  lightSrc,
  videoKey,
  isDark,
  className = '',
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  poster,
  onLoad,
  onError,
  threshold = 0.1,
  rootMargin = '150px',
  preload = 'metadata'
}: LazyVideoProps) {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Get optimized video URL
  const getVideoSrc = useCallback(() => {
    // If videoKey is provided, try to get optimized version
    if (videoKey) {
      const themeKey = isDark ? `${videoKey}-dark` : `${videoKey}-light`;
      const optimizedUrl = getOptimizedVideoUrl(themeKey, {
        isMobile: window.innerWidth < 768,
        isSlowConnection: (navigator as any).connection?.effectiveType && ['slow-2g', '2g', '3g'].includes((navigator as any).connection.effectiveType)
      });
      if (optimizedUrl) return optimizedUrl;
    }

    // Fallback to original URLs
    return isDark ? darkSrc : lightSrc;
  }, [videoKey, isDark, darkSrc, lightSrc]);

  // Update video source when theme changes
  useEffect(() => {
    const newSrc = getVideoSrc();
    if (newSrc !== currentSrc && isInView) {
      // Check if video is already cached
      const cachedUrl = getCachedVideo(newSrc);
      setCurrentSrc(cachedUrl || newSrc);
      setIsLoaded(false);
      setHasError(false);

      // Preload the video if not cached
      if (!cachedUrl) {
        videoPreloader.preloadVideo(newSrc, { priority: 'high' }).then(preloadedUrl => {
          if (preloadedUrl && newSrc === (isDark ? darkSrc : lightSrc)) {
            setCurrentSrc(preloadedUrl);
          }
        });
      }
    }
  }, [isDark, darkSrc, lightSrc, currentSrc, isInView]);

  // Handle video load success
  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
    logVideoLoadEnd(currentSrc, !!getCachedVideo(currentSrc));
    onLoad?.();
  }, [onLoad, currentSrc]);

  // Handle video load error
  const handleError = useCallback((_event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const error = new Error(`Failed to load video: ${currentSrc}`);
    setHasError(true);
    setIsLoaded(false);
    onError?.(error);
    console.warn('Video load error:', error);
  }, [currentSrc, onError]);

  // Set up intersection observer
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          const newSrc = getVideoSrc();

          // Check if video is already cached
          const cachedUrl = getCachedVideo(newSrc);
          setCurrentSrc(cachedUrl || newSrc);

          // Start performance monitoring
          logVideoLoadStart(newSrc);

          // Preload the video if not cached - with connection awareness
          if (!cachedUrl) {
            // Check connection speed before aggressive preloading
            const connection = (navigator as any).connection;
            const isSlowConnection = connection && ['slow-2g', '2g', '3g'].includes(connection.effectiveType);

            if (isSlowConnection) {
              // On slow connections, only preload metadata
              videoPreloader.preloadVideo(newSrc, { priority: 'medium', connectionThreshold: 'slow' });
            } else {
              // On fast connections, preload more aggressively
              videoPreloader.preloadVideo(newSrc, { priority: 'high' });
            }
          }

          // Disconnect observer after first intersection
          observerRef.current?.disconnect();
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observerRef.current.observe(video);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [isDark, darkSrc, lightSrc, videoKey, threshold, rootMargin, isInView, getVideoSrc]);

  // Handle video play/pause based on visibility and loading state
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isLoaded || hasError) return;

    if (autoPlay && isInView) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn('Video autoplay failed:', error);
        });
      }
    }
  }, [isLoaded, isInView, autoPlay, hasError]);

  return (
    <div className="relative w-full h-full">
      {/* Loading placeholder */}
      {!isLoaded && !hasError && isInView && (
        <div className="absolute inset-0 flex items-center justify-center bg-background-light/50 dark:bg-background-dark/50 rounded-xl">
          <div className="w-8 h-8 border-2 border-primary-light/30 dark:border-primary-dark/30 border-t-primary-light dark:border-t-primary-dark rounded-full animate-spin"></div>
        </div>
      )}

      {/* Error placeholder */}
      {hasError && (
        // <div className="absolute inset-0 flex items-center justify-center bg-background-light/50 dark:bg-background-dark/50 rounded-xl">
        //   <div className="text-center p-4">
        //     <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-primary-light/30 dark:bg-primary-dark/30 flex items-center justify-center">
        //       <Loader2 className="w-6 h-6 text-primary-light dark:text-primary-dark animate-spin" />
        //     </div>
        //     <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
        //       Loading content...
        //     </p>
        //   </div>
        // </div>
        <div className="absolute inset-0 flex items-center justify-center bg-background-light/50 dark:bg-background-dark/50 rounded-xl">
          <div className="w-8 h-8 border-2 border-primary-light/30 dark:border-primary-dark/30 border-t-primary-light dark:border-t-primary-dark rounded-full animate-spin"></div>
        </div>
      )}

      {/* Actual video element */}
      <video
        ref={videoRef}
        src={currentSrc}
        className={`w-full h-full object-cover transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
        autoPlay={false} // We handle autoplay manually
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        preload={preload}
        poster={poster}
        onLoadedData={handleLoad}
        onError={handleError}
        style={{
          // Performance optimizations
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          perspective: 1000,
          transform: 'translate3d(0, 0, 0)'
        }}
      />
    </div>
  );
} 